import React from 'react';
import { Check, X } from 'lucide-react';

// Requirements mirror the invite / reset flows so every password entry point
// asks for the same strength. Server enforces a subset (8+, letter, number);
// we ask for the stronger set client-side for better hygiene.
export const PASSWORD_REQUIREMENTS = [
  { id: 'length', label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { id: 'uppercase', label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { id: 'lowercase', label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
  { id: 'number', label: 'One number', test: (p) => /[0-9]/.test(p) },
  { id: 'special', label: 'One special character (!@#$%^&*)', test: (p) => /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(p) },
];

const LEVELS = [
  { min: 0, label: 'Too weak', color: '#B91C1C', track: 0 },
  { min: 1, label: 'Weak', color: '#DC6803', track: 1 },
  { min: 3, label: 'Fair', color: '#CA8A04', track: 2 },
  { min: 4, label: 'Good', color: '#3D7A2E', track: 3 },
  { min: 5, label: 'Strong', color: '#047857', track: 4 },
];

export const evaluatePassword = (pw = '') => {
  const checks = PASSWORD_REQUIREMENTS.map((r) => ({ ...r, passed: r.test(pw) }));
  const score = checks.filter((c) => c.passed).length;
  const level = [...LEVELS].reverse().find((l) => score >= l.min) || LEVELS[0];
  return { checks, score, allMet: score === PASSWORD_REQUIREMENTS.length, level };
};

/**
 * PasswordStrength — a segmented strength bar + live requirements checklist.
 * `extraRules` lets a caller append contextual checks (e.g. "passwords match",
 * "different from current password") into the same list.
 */
export const PasswordStrength = ({ password = '', extraRules = [], testId = 'password-strength' }) => {
  const { checks, score, level } = evaluatePassword(password);
  const active = password.length > 0;

  return (
    <div className="space-y-2.5" data-testid={testId}>
      <div className="flex items-center gap-2">
        <div className="flex-1 grid grid-cols-4 gap-1.5" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => (
            <span
              key={i}
              className="h-1.5 rounded-full transition-colors"
              style={{ background: active && i < level.track + (score >= 1 ? 1 : 0) ? level.color : 'rgba(0,0,0,0.08)' }}
            />
          ))}
        </div>
        <span
          className="text-xs font-semibold w-16 text-right"
          style={{ color: active ? level.color : '#8F8A7F' }}
          data-testid={`${testId}-label`}
        >
          {active ? level.label : '—'}
        </span>
      </div>

      <ul className="space-y-1 text-xs" data-testid={`${testId}-rules`}>
        {checks.map((c) => (
          <RuleRow key={c.id} met={c.passed} label={c.label} />
        ))}
        {extraRules.map((r, i) => (
          <RuleRow key={`extra-${i}`} met={r.passed} label={r.label} />
        ))}
      </ul>
    </div>
  );
};

const RuleRow = ({ met, label }) => (
  <li className={`flex items-center gap-2 ${met ? 'text-green-600' : 'text-muted-foreground'}`}>
    <span
      className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${met ? 'bg-green-600/15' : 'bg-muted-foreground/10'}`}
    >
      {met ? <Check className="w-3 h-3" /> : <X className="w-3 h-3 opacity-50" />}
    </span>
    {label}
  </li>
);

export default PasswordStrength;
